// Appointment utility functions

// Appointment time slots. These MUST correspond 1:1 with Delhivery's allowed
// appointment_slot values. The friendly value/label here is converted to
// Delhivery's exact format ("HH:MM AM-HH:MM PM", zero-padded, no spaces around
// the hyphen) in the backend at booking time (DelhiveryService.convert_time_range_to_slot).
// Delhivery allowed slots: 12:00 AM-07:00 AM, 07:00 AM-10:00 AM, 09:00 AM-12:00 PM,
// 09:00 AM-06:00 PM, 12:00 PM-03:00 PM, 03:00 PM-06:00 PM, 06:00 PM-09:00 PM, 09:00 PM-11:59 PM.
export const TIME_RANGES = [
  { value: "12:00 AM - 7:00 AM", label: "12:00 AM - 7:00 AM" },
  { value: "7:00 AM - 10:00 AM", label: "7:00 AM - 10:00 AM" },
  { value: "9:00 AM - 12:00 PM", label: "9:00 AM - 12:00 PM" },
  { value: "9:00 AM - 6:00 PM", label: "9:00 AM - 6:00 PM" },
  { value: "12:00 PM - 3:00 PM", label: "12:00 PM - 3:00 PM" },
  { value: "3:00 PM - 6:00 PM", label: "3:00 PM - 6:00 PM" },
  { value: "6:00 PM - 9:00 PM", label: "6:00 PM - 9:00 PM" },
  { value: "9:00 PM - 11:59 PM", label: "9:00 PM - 11:59 PM" }
];

export const APPOINTMENT_WARNING_MESSAGE = "*Appointments for tomorrow must be booked before 6:00 PM today*";

/** True if the order's appointment has been booked with Delhivery. Checks (in order):
 *  the delhivery_appointment_booked flag, a stored delhivery_appointment_id, or an
 *  APPOINTMENT_SCHEDULED status-history entry (covers older bookings where the id
 *  wasn't captured). */
export const isDelhiveryAppointmentBooked = (order) =>
  !!(
    order?.delhivery_appointment_booked ||
    order?.delhivery_appointment_id ||
    (order?.status_history || []).some((h) => h?.status === "APPOINTMENT_SCHEDULED")
  );

// ---- PO expiry ↔ appointment rules (mirror backend utils/po_validation.py) ----
// The PO Expiry Date for the appointment rule is the ORDER-LEVEL po_expiry_date only.
// A reference's `date` is the reference/PO date, NOT an expiry — it must not gate
// appointments (folding it in wrongly rejected valid bookings).

// Coerce a date/ISO-datetime value to a 'YYYY-MM-DD' string ('' if empty).
const normDate = (v) => (v ? String(v).trim().slice(0, 10) : "");

/** The order's PO expiry (YYYY-MM-DD) for the appointment rule = po_expiry_date only. */
export const getEarliestPoExpiry = (referenceNumbers, poExpiryDate) => normDate(poExpiryDate) || null;

/** R2: appointment must not be later than the order's PO Expiry Date (po_expiry_date).
 *  Returns an error string or null. Skips when no appointment or no expiry is set. */
export const validateAppointmentWithinPoExpiry = (referenceNumbers, appointmentDate, poExpiryDate) => {
  const appt = normDate(appointmentDate);
  const expiry = normDate(poExpiryDate);
  if (appt && expiry && appt > expiry) {
    return `Appointment date cannot be later than the PO expiry date (${expiry}).`;
  }
  return null;
};

/** For the order create forms: a reference number requires a reference date.
 *  (The appointment-vs-PO-expiry rule keys off the order-level po_expiry_date, which
 *  the create form doesn't set, so it isn't enforced here.) Returns error/null. */
export const validatePoExpiryAndAppointment = (referenceNumbers, appointmentDate) => {
  for (const r of referenceNumbers || []) {
    const rn = (r?.reference_number || "").trim();
    const exp = (r?.date || "").trim();
    if (rn && !exp) return `Date is required for reference number "${rn}".`;
  }
  return null;
};

/**
 * Order-level PO Expiry Date rule: whenever a reference/PO number is entered, the
 * order-level PO Expiry Date is mandatory (and the appointment, if any, must not be
 * later than it). Returns an error string, or null when valid. `poExpiryDate` and
 * `appointmentDate` are 'YYYY-MM-DD' strings (which sort chronologically).
 */
export const validateOrderPoExpiry = (referenceNumbers, poExpiryDate, appointmentDate) => {
  const hasRef = (referenceNumbers || []).some((r) => (r?.reference_number || "").trim());
  const exp = (poExpiryDate || "").trim();
  if (hasRef && !exp) {
    return "PO Expiry Date is required when a reference/PO number is entered.";
  }
  const appt = (appointmentDate || "").trim();
  if (exp && appt && appt > exp) {
    return `Appointment date (${appt}) cannot be later than the PO expiry date (${exp}).`;
  }
  return null;
};

/** R3: when an appointment date is set, at least one reference/PO number is required.
 *  The reverse is NOT enforced — a reference alone needs no appointment. Combined with
 *  validatePoExpiryAndAppointment + validateOrderPoExpiry this gives the full chain
 *  appointment => reference => (reference date + PO expiry). Returns an error string or null.
 *  Mirrors backend validate_reference_required_for_appointment. */
export const validateReferenceRequiredForAppointment = (referenceNumbers, appointmentDate) => {
  const appt = (appointmentDate || "").trim();
  if (!appt) return null;
  const hasRef = (referenceNumbers || []).some((r) => (r?.reference_number || "").trim());
  if (!hasRef) return "A reference/PO number is required when an appointment date is set.";
  return null;
};

/**
 * Get the minimum date for appointment booking
 * RULE: No appointments for today - ONLY tomorrow onwards
 * If current time is before 6 PM (18:00), appointments can be booked from tomorrow
 * If current time is after 6 PM (18:00), tomorrow is closed - can book from day after tomorrow
 */
export const getMinAppointmentDate = () => {
  const now = new Date();
  const currentHour = now.getHours();
  
  // Never allow today - minimum is tomorrow
  // After 6 PM, tomorrow is also closed - minimum becomes day after tomorrow
  const daysToAdd = currentHour >= 18 ? 2 : 1;
  
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + daysToAdd);
  minDate.setHours(0, 0, 0, 0);
  
  return minDate;
};

/**
 * Get today's date for comparison (to block current date selection)
 */
export const getTodayDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * Get the maximum date for appointment booking (15 days from today)
 */
export const getMaxAppointmentDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 15);
  maxDate.setHours(23, 59, 59, 999);
  
  return maxDate;
};

/**
 * Check if a date is valid for appointment booking
 * - Cannot be today
 * - Must be at least tomorrow (or day after if past 6 PM)
 * - Cannot be more than 15 days in future
 */
export const isValidAppointmentDate = (date) => {
  if (!date) return false;
  
  const selectedDate = new Date(date);
  selectedDate.setHours(0, 0, 0, 0);
  
  const today = getTodayDate();
  const minDate = getMinAppointmentDate();
  const maxDate = getMaxAppointmentDate();
  
  // Explicitly check: cannot be today
  if (selectedDate.getTime() === today.getTime()) {
    return false;
  }
  
  return selectedDate >= minDate && selectedDate <= maxDate;
};

/**
 * Get formatted warning message based on current time
 */
export const getAppointmentWarningMessage = () => {
  const now = new Date();
  const currentHour = now.getHours();
  
  if (currentHour >= 18) {
    return "⚠️ *Appointments for tomorrow are closed (booking deadline was 6:00 PM today). You can book from day after tomorrow onwards.*";
  }
  
  return "ℹ️ *Appointments for tomorrow must be booked before 6:00 PM today.*";
};
